import { CalendarClock, CalendarPlus, Check, Clock3, Copy, EyeOff, LoaderCircle, Pencil, Play, RefreshCw, ShieldCheck, Trash2, UsersRound, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { AppShell } from "../components/AppShell";
import { ModalDialog } from "../components/ModalDialog";
import { StatusNotice } from "../components/StatusNotice";
import { useAuth } from "../features/auth/AuthContext";
import { InvitationDialog } from "../features/room/InvitationDialog";
import { savePrejoinTicket } from "../features/room/roomSession";
import { api, getApiErrorMessage } from "../lib/api";
import { authContinuationFromPath, authContinuationPath } from "../lib/authContinuation";
import { talkBrand } from "../lib/brand";
import { roleLabel, ui, uiLocale } from "../lib/ui";
import { clearScheduleDraft, loadScheduleDraft, saveScheduleDraft, type ScheduleDraft } from "../lib/scheduleDraft";
import { combineLocalDateTime, defaultScheduledInput, formatMeetingTime, formatZonedDateTime, formatZonedTime, localDateTimeParts, utcToZonedInput, viewerTimeZone, zonedDateTimeToUTC } from "../lib/scheduling";
import { loadPreferences } from "../lib/preferences";
import type { RoomHistoryItem, TalkRoom } from "../lib/types";

const currentTimeZone = viewerTimeZone();
const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, "0"));
const minutes = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));
const supportedTimeZones = (() => {
  const values = (Intl as typeof Intl & { supportedValuesOf?: (key: "timeZone") => string[] }).supportedValuesOf?.("timeZone") ?? [];
  return values.includes(currentTimeZone) ? values : [currentTimeZone, ...values];
})();

type ScheduleFields = ScheduleDraft;

function shownMessage(message: string): string {
  const copy: Record<string, string> = {
    "Choose a valid date and time.": "Выберите корректные дату и время.",
    "That local time does not exist in the selected time zone. Choose another time.": "Такого местного времени в выбранном часовом поясе нет. Выберите другое.",
    "Talk took too long to respond. Try again.": "Ответ шёл слишком долго. Попробуйте ещё раз.",
    "The request was cancelled.": "Запрос отменён.",
    "Talk is unreachable. Check your connection and try again.": "Сервер недоступен. Проверьте соединение и попробуйте ещё раз.",
    "Something went wrong. Try again.": "Что-то пошло не так. Попробуйте ещё раз.",
  };
  const translated = copy[message];
  return translated ? ui(message, translated) : message;
}

function meetingTimeLabel(room: TalkRoom): string {
  if (!room.scheduledAt) return ui("Available now", "Можно войти сейчас");
  return formatMeetingTime(room, uiLocale());
}

function initialFields(name = ""): ScheduleFields {
  return {
    title: name ? ui(`${name}'s meeting`, `Партия ${name}`) : talkBrand.id === "chess" ? "Партия" : talkBrand.neutralAvatars ? "Operations briefing" : "CTF briefing",
    localTime: defaultScheduledInput(),
    timeZone: currentTimeZone,
    allowGuests: true,
    haramModeDefault: false,
  };
}

export function SchedulePage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const accountName = auth.snapshot?.user.displayName || auth.snapshot?.user.handle || "";
  const [fields, setFields] = useState<ScheduleFields>(() => loadScheduleDraft() ?? initialFields(accountName));
  const [items, setItems] = useState<RoomHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState<TalkRoom | null>(null);
  const [cancelRoom, setCancelRoom] = useState<TalkRoom | null>(null);
  const [invite, setInvite] = useState<{ room: TalkRoom; code: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const loadUpcoming = useCallback(async (signal?: AbortSignal) => {
    if (!auth.snapshot) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await api.rooms.upcoming(signal);
      setItems(result.items);
      setError("");
    } catch (nextError) {
      if (!signal?.aborted) setError(getApiErrorMessage(nextError));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [auth.snapshot]);

  useEffect(() => {
    if (auth.loading) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => { void loadUpcoming(controller.signal); }, 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [auth.loading, loadUpcoming]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  function update(patch: Partial<ScheduleFields>) {
    setFields((current) => {
      const next = { ...current, ...patch };
      if (!editing) saveScheduleDraft(next);
      return next;
    });
    setError("");
    setSuccess("");
  }

  async function submitSchedule() {
    if (!auth.snapshot) {
      saveScheduleDraft(fields);
      navigate("/login", { state: { from: authContinuationPath("schedule") } });
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const scheduledAt = zonedDateTimeToUTC(fields.localTime, fields.timeZone);
      if (editing) {
        const room = await api.rooms.update(editing.id, { title: fields.title.trim(), allowGuests: fields.allowGuests, haramModeDefault: talkBrand.supportsHaramMode ? fields.haramModeDefault : false, scheduledAt, scheduledTimeZone: fields.timeZone });
        setEditing(null);
        setSuccess(ui("Meeting updated. The reusable link did not change.", "Партия обновлена. Постоянная ссылка не изменилась."));
        setItems((current) => current.map((item) => item.room.id === room.id ? { ...item, room } : item));
      } else {
        const created = await api.rooms.create({ title: fields.title.trim(), allowGuests: fields.allowGuests, haramModeDefault: talkBrand.supportsHaramMode ? fields.haramModeDefault : false, participantLimit: 12, scheduledAt, timeZone: fields.timeZone });
        setSuccess(ui("Meeting scheduled. Share the reusable invitation whenever you are ready.", "Партия запланирована. Постоянным приглашением можно поделиться, когда будете готовы."));
        setInvite({ room: created.room, code: created.code });
        setFields(initialFields(accountName));
        clearScheduleDraft();
        if (authContinuationFromPath(`${location.pathname}${location.search}`) === "schedule") navigate("/schedule", { replace: true });
        await loadUpcoming();
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : getApiErrorMessage(nextError));
    } finally {
      setSaving(false);
    }
  }

  function editRoom(room: TalkRoom) {
    const zone = room.scheduledTimeZone || currentTimeZone;
    setEditing(room);
    setFields({
      title: room.title,
      localTime: room.scheduledAt ? utcToZonedInput(room.scheduledAt, zone) : defaultScheduledInput(),
      timeZone: zone,
      allowGuests: room.allowGuests,
      haramModeDefault: talkBrand.supportsHaramMode && room.haramModeDefault,
    });
    setError("");
    setSuccess("");
    const reducedMotion = loadPreferences().reducedMotion || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
  }

  async function confirmCancel() {
    if (!cancelRoom) return;
    setSaving(true);
    setError("");
    try {
      await api.rooms.cancel(cancelRoom.id);
      setItems((current) => current.filter((item) => item.room.id !== cancelRoom.id));
      if (editing?.id === cancelRoom.id) {
        setEditing(null);
        setFields(initialFields(accountName));
      }
      setCancelRoom(null);
      setSuccess(ui("Meeting cancelled. Its invitation can no longer be used.", "Партия отменена. Её приглашением больше нельзя воспользоваться."));
    } catch (nextError) {
      setError(getApiErrorMessage(nextError));
    } finally {
      setSaving(false);
    }
  }

  async function openInvite(room: TalkRoom) {
    setError("");
    try {
      const code = await api.rooms.invitationCode(room.id);
      setInvite({ room, code });
    } catch (nextError) {
      setError(getApiErrorMessage(nextError));
    }
  }

  async function enterRoom(room: TalkRoom) {
    setError("");
    try {
      const resolved = await api.rooms.accountPrejoin(room.id);
      savePrejoinTicket(room.id, resolved.prejoinTicket);
      navigate(`/r/${encodeURIComponent(room.id)}/prejoin`, { state: { room: resolved.room } });
    } catch (nextError) {
      setError(getApiErrorMessage(nextError));
    }
  }

  const entryOpen = (room: TalkRoom) => room.state !== "scheduled" || Boolean(room.earlyEntryAt && now >= Date.parse(room.earlyEntryAt));
  const continuation = authContinuationFromPath(`${location.pathname}${location.search}`);
  const scheduleLabel = !auth.snapshot ? ui("Sign in and continue", "Войти и продолжить") : editing ? ui("Update meeting", "Обновить партию") : ui("Schedule meeting", "Запланировать партию");
  const localParts = localDateTimeParts(fields.localTime);
  const [selectedHour = "", selectedMinute = ""] = localParts.time.split(":");
  const schedulePreview = (() => {
    try {
      const scheduledAt = zonedDateTimeToUTC(fields.localTime, fields.timeZone);
      return { scheduledAt, earlyEntryAt: new Date(Date.parse(scheduledAt) - 15 * 60_000).toISOString() };
    } catch {
      return null;
    }
  })();

  return (
    <AppShell>
      <main id="main-content" className="content-page schedule-page" tabIndex={-1}>
        <header className="content-heading schedule-heading"><div><h1>{ui("Make the room ready before the call.", "Подготовьте комнату до звонка.")}</h1><p>{ui("Choose the time, access, and room identity. The reusable invitation link remains valid through reschedules.", "Выберите время, доступ и название комнаты. Постоянная ссылка-приглашение сохраняется, если партию переносят.")}</p></div></header>

        <div className="schedule-layout">
          <section className="schedule-form" aria-labelledby="schedule-form-title">
            <div className="schedule-form__title"><span><CalendarPlus aria-hidden="true" /></span><div><h2 id="schedule-form-title">{editing ? ui("Reschedule meeting", "Перенести партию") : ui("New meeting", "Новая партия")}</h2><p>{editing ? ui("The room code and link stay unchanged.", "Код комнаты и ссылка не меняются.") : ui("Entry opens 15 minutes before the scheduled time.", "Вход открывается за 15 минут до назначенного времени.")}</p></div>{editing ? <button type="button" aria-label={ui("Stop editing", "Закончить правку")} onClick={() => { setEditing(null); setFields(initialFields(accountName)); }}><X aria-hidden="true" /></button> : null}</div>
            {!auth.loading && !auth.snapshot ? <StatusNotice tone="warning" title={ui("Sign in to schedule", "Войдите, чтобы запланировать")}>{ui("Fill the form first if you like. Your draft stays in this browser while you sign in.", "Форму можно заполнить заранее. Черновик останется в этом браузере, пока вы входите.")}</StatusNotice> : null}
            {auth.snapshot && continuation === "schedule" ? <StatusNotice tone="success" title={ui("Signed in — your draft is ready", "Вы вошли — черновик на месте")}>{ui("Review the details below, then schedule the meeting when you are ready.", "Проверьте данные ниже и запланируйте партию, когда будете готовы.")}</StatusNotice> : null}
            <label className="field"><span>{ui("Meeting title", "Название партии")}</span><input maxLength={100} value={fields.title} onChange={(event) => update({ title: event.target.value })} placeholder={ui("Red-team briefing", "Разбор партии")} /></label>
            <div className="schedule-time-grid">
              <label className="field"><span>{ui("Date", "Дата")}</span><input type="date" value={localParts.date} onChange={(event) => update({ localTime: combineLocalDateTime(event.target.value, localParts.time) })} /></label>
              <fieldset className="field schedule-time-field"><legend>{ui("Time", "Время")} <small>{ui("24-hour", "24 часа")}</small></legend><div className="schedule-time-selects"><label><span className="sr-only">{ui("Hour", "Час")}</span><select aria-label={ui("Hour", "Час")} value={selectedHour} onChange={(event) => update({ localTime: combineLocalDateTime(localParts.date, `${event.target.value}:${selectedMinute}`) })}>{hours.map((hour) => <option value={hour} key={hour}>{hour}</option>)}</select></label><span aria-hidden="true">:</span><label><span className="sr-only">{ui("Minute", "Минута")}</span><select aria-label={ui("Minute", "Минута")} value={selectedMinute} onChange={(event) => update({ localTime: combineLocalDateTime(localParts.date, `${selectedHour}:${event.target.value}`) })}>{minutes.map((minute) => <option value={minute} key={minute}>{minute}</option>)}</select></label></div></fieldset>
              <label className="field"><span>{ui("Time zone", "Часовой пояс")}</span><input list="talk-time-zones" value={fields.timeZone} onChange={(event) => update({ timeZone: event.target.value })} autoComplete="off" /><datalist id="talk-time-zones">{supportedTimeZones.map((zone) => <option value={zone} key={zone} />)}</datalist></label>
            </div>
            {schedulePreview ? <div className="schedule-time-summary"><CalendarClock aria-hidden="true" /><div><strong>{formatZonedDateTime(schedulePreview.scheduledAt, fields.timeZone, uiLocale())} · {fields.timeZone}</strong><span>{ui(`Entry opens at ${formatZonedTime(schedulePreview.earlyEntryAt, fields.timeZone)}`, `Вход откроется в ${formatZonedTime(schedulePreview.earlyEntryAt, fields.timeZone, uiLocale())}`)}{fields.timeZone !== currentTimeZone ? ui(` · Your time: ${formatZonedDateTime(schedulePreview.scheduledAt, currentTimeZone)}`, ` · Ваше время: ${formatZonedDateTime(schedulePreview.scheduledAt, currentTimeZone, uiLocale())}`) : ""}</span></div></div> : null}
            <div className="room-policy-options" aria-label={ui("Room defaults", "Параметры комнаты")}>
              <label className="guest-policy"><input type="checkbox" checked={fields.allowGuests} onChange={(event) => update({ allowGuests: event.target.checked })} /><span><UsersRound aria-hidden="true" /><span><strong>{ui("Allow guests with the link", "Пускать гостей по ссылке")}</strong><small>{ui("Turn this off when every attendee should sign in.", "Выключите, если каждый должен войти в аккаунт.")}</small></span></span><Check aria-hidden="true" /></label>
              {talkBrand.supportsHaramMode ? <label className="guest-policy"><input type="checkbox" checked={fields.haramModeDefault} onChange={(event) => update({ haramModeDefault: event.target.checked })} /><span><EyeOff aria-hidden="true" /><span><strong>Start with Haram mode</strong><small>Participants initially see modest hijab avatars, but can change their own view.</small></span></span><Check aria-hidden="true" /></label> : null}
            </div>
            {error ? <StatusNotice tone="danger" title={ui("Could not save meeting", "Не удалось сохранить партию")}>{shownMessage(error)}</StatusNotice> : null}
            {success ? <StatusNotice tone="success" title={ui("Meeting ready", "Партия готова")}>{success}</StatusNotice> : null}
            <button className="button button--primary schedule-submit" type="button" onClick={() => void submitSchedule()} disabled={saving || !fields.title.trim() || !fields.localTime || !fields.timeZone}>{saving ? <LoaderCircle className="spin" aria-hidden="true" /> : <CalendarPlus aria-hidden="true" />}{saving ? ui("Saving meeting…", "Сохраняем партию…") : scheduleLabel}</button>
            <p className="schedule-assurance"><ShieldCheck aria-hidden="true" />{ui("Scheduling creates no media room until the early-entry window begins.", "Комната со звуком и видео не создаётся, пока не откроется ранний вход.")}</p>
          </section>

          <section className="upcoming-meetings" aria-labelledby="upcoming-title">
            <div className="upcoming-heading"><div><h2 id="upcoming-title">{ui("Upcoming", "Ближайшие")}</h2><p>{ui("Your scheduled and currently open planned rooms.", "Запланированные комнаты и те, что уже открыты.")}</p></div><button type="button" aria-label={ui("Refresh upcoming meetings", "Обновить список")} onClick={() => void loadUpcoming()} disabled={loading}><RefreshCw className={loading ? "spin" : ""} aria-hidden="true" /></button></div>
            {loading ? <div className="upcoming-loading" role="status"><LoaderCircle className="spin" aria-hidden="true" />{ui("Loading meetings…", "Загружаем партии…")}</div> : items.length === 0 ? <div className="upcoming-empty"><CalendarClock aria-hidden="true" /><strong>{ui("No meetings scheduled", "Партий пока нет")}</strong><span>{ui("Your next reusable room will appear here.", "Следующая постоянная комната появится здесь.")}</span></div> : <div className="upcoming-list">{items.map(({ room, role }) => {
              const canEnter = entryOpen(room);
              const zone = room.scheduledTimeZone || "UTC";
              return <article key={room.id} className={canEnter ? "is-entry-open" : ""}><div className="upcoming-time"><Clock3 aria-hidden="true" /><span>{meetingTimeLabel(room)}</span><small>{canEnter ? room.state === "scheduled" ? ui("Early entry is open", "Ранний вход открыт") : ui("Room is open", "Комната открыта") : room.earlyEntryAt ? ui(`Entry opens ${formatZonedTime(room.earlyEntryAt, zone)} · ${zone}`, `Вход откроется ${formatZonedTime(room.earlyEntryAt, zone, uiLocale())} · ${zone}`) : ui("Entry opens 15 minutes early", "Вход откроется за 15 минут")}</small></div><div className="upcoming-title"><h3>{room.title}</h3><span>{room.allowGuests ? ui("Guests allowed", "Гости могут войти") : ui("Account required", "Нужен аккаунт")}{talkBrand.supportsHaramMode && room.haramModeDefault ? " · Haram default" : ""} · {roleLabel(role)}</span></div><div className="upcoming-actions"><button type="button" onClick={() => void openInvite(room)}><Copy aria-hidden="true" />{ui("Invite", "Пригласить")}</button>{room.state === "scheduled" ? <button type="button" onClick={() => editRoom(room)}><Pencil aria-hidden="true" />{ui("Edit", "Изменить")}</button> : null}<button className="is-primary" type="button" onClick={() => void enterRoom(room)} disabled={!canEnter}><Play aria-hidden="true" />{canEnter ? ui("Enter", "Войти") : ui("Not yet", "Рано")}</button>{room.state === "scheduled" ? <button className="is-danger" type="button" aria-label={ui(`Cancel ${room.title}`, `Отменить ${room.title}`)} onClick={() => setCancelRoom(room)}><Trash2 aria-hidden="true" /></button> : null}</div></article>;
            })}</div>}
          </section>
        </div>

        {invite ? <InvitationDialog room={invite.room} code={invite.code} participantCount={invite.room.participantCount} onClose={() => setInvite(null)} /> : null}
        {cancelRoom ? <ModalDialog labelledBy="cancel-meeting-title" className="confirm-dialog" onClose={() => setCancelRoom(null)}><h2 id="cancel-meeting-title">{ui(`Cancel ${cancelRoom.title}?`, `Отменить ${cancelRoom.title}?`)}</h2><p>{ui("The reusable link will stop working immediately. This cannot be undone.", "Постоянная ссылка сразу перестанет работать. Это нельзя отменить.")}</p><div className="dialog-actions"><button className="button button--secondary" type="button" data-autofocus onClick={() => setCancelRoom(null)}>{ui("Keep meeting", "Оставить партию")}</button><button className="button button--danger" type="button" onClick={() => void confirmCancel()} disabled={saving}><Trash2 aria-hidden="true" />{ui("Cancel meeting", "Отменить партию")}</button></div></ModalDialog> : null}
      </main>
    </AppShell>
  );
}
